/* bp-hero-collections — scroll-driven pinned collection reveal.
   Sticky positioning does the pinning (native scroll, no scroll-jacking).
   Acts SNAP between states rather than crossfading: each act holds while you
   scroll through its band, then flips to the next at the midpoint with a short
   CSS transition. Jordan's call 2026-08-05 - the continuous blur/fade read as
   mushy; he wants it snappy. */
(function () {
  'use strict';

  var SNAP_MS = 160; // how fast one act replaces the next

  class BpHeroCollections extends HTMLElement {
    connectedCallback() {
      this.acts = Array.from(this.querySelectorAll('.bp-hero__act'));
      this.ticks = Array.from(this.querySelectorAll('.bp-hero__tick'));
      if (this.acts.length === 0) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduced.matches) return; // markup ships static; leave it that way
      this.classList.remove('bp-hero--static');
      this.setAttribute('data-motion', '');
      this.current = -1;
      this.inView = true;
      this.ticking = false;

      this.onScroll = () => {
        if (this.ticking || !this.inView) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      };

      this.observer = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.update();
        /* Safety net: update() stops running once we are out of view, so
           release the header lock here too rather than leaving it stuck. */
        else this.setHeaderLock(false);
      });
      this.observer.observe(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.preloadImages();
      this.update();
    }

    /* Fire only on change, so we are not spamming events every scroll frame. */
    setHeaderLock(on) {
      if (on === this.headerLocked) return;
      this.headerLocked = on;
      document.dispatchEvent(new CustomEvent(on ? 'bp:header-reveal-lock' : 'bp:header-reveal-unlock'));
    }

    disconnectedCallback() {
      this.setHeaderLock(false);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.observer) this.observer.disconnect();
    }

    /* Warm every act's image for the current breakpoint immediately.
       Without this, lazy-loaded act 2/3 images pop in mid-scroll. */
    preloadImages() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const sel = mobile ? '.bp-hero__media--mobile img' : '.bp-hero__media--desktop img';
      this.querySelectorAll(sel).forEach((img) => {
        if (img.loading === 'lazy') img.loading = 'eager';
        if (img.decode) img.decode().catch(() => {});
      });
    }

    update() {
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight;
      const scrollable = rect.height - vh;
      const n = this.acts.length;
      if (scrollable <= 0) {
        this.setHeaderLock(false);
        this.applyOpacities(0);
        this.setActive(0);
        return;
      }
      const progress = Math.min(Math.max(-rect.top / scrollable, 0), 1);
      /* While the hero is pinned, hold the sticky header down. Scrolling up
         inside the acts otherwise re-reveals the nav over the top of the art.
         Jordan's call 2026-08-16. Engaged = pinned to the viewport: the top
         edge has passed and the runway has not finished. */
      this.setHeaderLock(rect.top <= 0 && rect.bottom > vh);
      /* EQUAL band per act. The old mapping (progress * (n-1) then rounding) gave
         the first and last acts a HALF-width band and the middle acts a full one,
         so the middle acts felt twice as long to scroll through. Flooring
         progress * n gives every act exactly 1/n of the runway. */
      const idx = Math.min(Math.floor(progress * n), n - 1);
      this.applySnap(idx);
      this.setActive(idx);
    }

    /* Discrete snap: the active act is the only visible one. No scrubbing,
       no partial opacity, no scale drift - it holds, then flips. */
    applySnap(idx) {
      const n = this.acts.length;
      idx = Math.min(Math.max(idx, 0), n - 1);
      if (idx === this.snapped) return;
      this.snapped = idx;
      for (let i = 0; i < n; i++) {
        const on = i === idx;
        this.acts[i].style.setProperty('--act-o', on ? '1' : '0');
        this.acts[i].style.setProperty('--act-scale', '1');
        this.acts[i].style.zIndex = on ? '1' : '0';
      }
    }

    setActive(idx) {
      idx = Math.min(Math.max(idx, 0), this.acts.length - 1);
      if (idx === this.current) return;
      this.current = idx;
      this.acts.forEach((act, i) => {
        act.classList.toggle('is-active', i === idx);
        act.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
        act.querySelectorAll('a, button').forEach((el) => {
          if (i === idx) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
      this.ticks.forEach((tick, i) => {
        tick.classList.toggle('is-active', i === idx);
        if (i === idx) tick.setAttribute('aria-current', 'true');
        else tick.removeAttribute('aria-current');
      });
    }
  }

  if (!customElements.get('bp-hero-collections')) {
    customElements.define('bp-hero-collections', BpHeroCollections);
  }
})();
